import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { FileText, ShoppingCart, XCircle, Tag, Package, Shield, Scale, ChevronRight, Info } from 'lucide-react';

const sectionIcons = {
  purchase: ShoppingCart,
  cancellation: XCircle,
  pricing: Tag,
  provision: Package,
  warranty: Shield,
  liability: Scale
};

// Helper function to format content with line breaks
const FormattedContent = ({ content }) => {
  return (
    <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
      {content}
    </div>
  );
};

export const TermsSection = ({ formData, setFormData, errors, t }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const terms = t.terms;

  const handleTermsAcceptance = (checked) => {
    setFormData(prev => ({
      ...prev,
      termsAccepted: checked
    }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <FileText className="w-5 h-5 text-primary" />
            {terms.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Intro Text */}
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <p className="text-sm text-foreground">{terms.intro}</p>
            <p className="text-sm text-muted-foreground mt-2">{terms.acceptance}</p>
          </div>

          {/* View Full Terms Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                <FileText className="w-4 h-4 mr-2" />
                {terms.viewTerms}
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  {terms.title}
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="h-[75vh] pr-4">
                <div className="space-y-6">
                  {/* Introduction Section */}
                  {terms.introduction && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="w-4 h-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">{terms.introduction.title}</h3>
                      </div>
                      <FormattedContent content={terms.introduction.content} />
                      <Separator className="my-4" />
                    </div>
                  )}
                  
                  {/* All Terms Sections - No Accordion */}
                  {Object.entries(terms.sections).map(([key, section], index) => {
                    const Icon = sectionIcons[key];
                    return (
                      <div key={key}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="w-4 h-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">{section.title}</h3>
                        </div>
                        <FormattedContent content={section.content} />
                        {index < Object.entries(terms.sections).length - 1 && (
                          <Separator className="my-4" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {/* Terms Acceptance Checkbox */}
          <div className={`p-4 rounded-lg border-2 transition-colors ${
            formData.termsAccepted 
              ? 'bg-success/5 border-success/30' 
              : errors.termsAccepted 
              ? 'bg-destructive/5 border-destructive/30'
              : 'bg-card border-border'
          }`}>
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={formData.termsAccepted}
                onCheckedChange={handleTermsAcceptance}
                className="mt-0.5"
              />
              <div>
                <Label 
                  htmlFor="terms" 
                  className="text-sm font-medium cursor-pointer leading-relaxed"
                >
                  {terms.checkbox} <span className="text-destructive">*</span>
                </Label>
                {errors.termsAccepted && (
                  <p className="text-xs text-destructive mt-1">{errors.termsAccepted}</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsSection;
